import _ from 'underscore';
import { useCallback } from 'react';

import { parseUrlSearchAndHashParams } from './parse-url-search-and-hash-params';

const defaultActivityLogger = {
  trace: _.noop,
  error: _.noop,
};

const POPUP_WINDOW_WIDTH = 600;
const POPUP_WINDOW_HEIGHT = 680;

const defaultMatcher = {
  isAuthCallback: 1,
};

/**
 * Provides a function to open a popup window and wait for it to return an authorization signal in return url.
 * @param {Window} windowObject - Parent window that will be used to open the popup and receive the message.
 * @param {Object} activity - Activity logger.
 * @returns {Function} - Function to open the window and wait for the message, returns a Promise.
 */
export function useOpenPopupAndWaitForAuth({ windowObject = window, activity = defaultActivityLogger } = {}) {
  const getPopupWindowFeatures = useCallback((
    width = POPUP_WINDOW_WIDTH,
    height = POPUP_WINDOW_HEIGHT
  ) => {
    // calculate center point of parent window
    const centerX = windowObject.screenX + (windowObject.outerWidth / 2);
    const centerY = windowObject.screenY + (windowObject.outerHeight / 2);

    // set left and top so that popup is centered over the parent window
    const left = centerX - (width / 2);
    const top = centerY - (height / 2);

    const features = [
      `width=${width}`,
      `height=${height}`,
      `left=${left}`,
      `top=${top}`,
      'scrollbars=1',
      'toolbar=0',
    ];
    return features.join(',');
  }, [windowObject]);

  const openPopupAndWaitForAuth = useCallback(({
    url, width, height, matcher = defaultMatcher,
  }) => {
    activity.trace(`Opening pop-up window to ${url}...`);

    const popupWindowFeatures = getPopupWindowFeatures(width, height);
    // Returns a promise that will resolve with the matching authorization signal. If the window is
    // closed before authorization completes, promise will reject with an error.
    return new Promise((resolve, reject) => {
      const popupWindow = windowObject.open(url, '', popupWindowFeatures);
      popupWindow.focus();
      const timer = setInterval(() => {
        if (popupWindow.closed) {
          activity.trace('Pop-up window was closed, signal was never received.');
          window.clearInterval(timer);
          reject(new Error('popup_closed_by_user'));
        } else {
          let params = null;
          try {
            params = parseUrlSearchAndHashParams(popupWindow.location.href);
          } catch {
            // ignore errors
          }
          if (params && _.isMatch(params, matcher)) {
            activity.trace(`Found a matching auth signal. keys=[${_.keys(params).join(', ')}]`);
            window.clearInterval(timer);
            popupWindow.close();
            resolve(params);
          }
        }
      }, 100);
    });
  }, [windowObject, activity, getPopupWindowFeatures]);

  return openPopupAndWaitForAuth;
}
